/**
 * Comparisons Hub Page - Browse all study abroad comparisons
 * /compare
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import SEO from '@/components/SEO';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Scale,
  Search,
  ChevronRight,
  Star,
  Loader2,
  Globe,
  GraduationCap,
  BookOpen,
  ArrowLeftRight
} from 'lucide-react';

const API_URL = window.location.origin || '';

const COMPARISON_TYPES = {
  destination: { label: 'Destination Comparisons', icon: Globe, color: 'text-blue-500' },
  program: { label: 'Program Comparisons', icon: BookOpen, color: 'text-green-500' },
  degree: { label: 'Degree Comparisons', icon: GraduationCap, color: 'text-purple-500' }
};

const ComparisonsPage = () => {
  const { isDark } = useTheme();
  const [comparisons, setComparisons] = useState([]);
  const [filteredComparisons, setFilteredComparisons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');

  useEffect(() => {
    const fetchComparisons = async () => {
      try {
        const res = await fetch(`${API_URL}/api/comparisons/public`);
        if (res.ok) {
          const data = await res.json();
          setComparisons(data);
          setFilteredComparisons(data);
        }
      } catch (error) {
        console.error('Error fetching comparisons:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchComparisons();
  }, []);

  // Filter comparisons
  useEffect(() => {
    let filtered = [...comparisons];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        c.title.toLowerCase().includes(term) ||
        c.tagline.toLowerCase().includes(term)
      );
    }

    if (selectedType !== 'all') {
      filtered = filtered.filter(c => c.comparison_type === selectedType);
    }

    setFilteredComparisons(filtered);
  }, [searchTerm, selectedType, comparisons]);

  // Group comparisons by type
  const groupedComparisons = filteredComparisons.reduce((acc, comp) => {
    const type = comp.comparison_type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(comp);
    return acc;
  }, {});

  const featuredComparisons = comparisons.filter(c => c.is_featured).slice(0, 3);

  return (
    <>
      <SEO 
        title="Study Abroad Comparisons - USA vs UK vs Canada, MBA vs MS"
        description="Compare study abroad destinations and programs. Side-by-side analysis of MS in USA vs UK vs Canada, MBA vs MS, Canada vs Australia, and more to help Indian students choose."
        keywords="study abroad comparison, USA vs UK, USA vs Canada, MBA vs MS, Canada vs Australia, which country is best for masters, compare universities abroad"
        canonical="https://unixperts.com/compare"
      />
      
      <div className={`min-h-screen pt-20 ${isDark ? 'bg-[#1a2830]' : 'bg-slate-50'}`}>
        {/* Hero Section */}
        <section className={`py-12 md:py-20 ${isDark ? 'bg-gradient-to-br from-[#1a2830] to-[#0d1117]' : 'bg-gradient-to-br from-purple-600 to-blue-600'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Badge className="bg-white/20 text-white mb-4" data-testid="comparisons-badge">
              <Scale className="w-3 h-3 mr-1" />
              Compare & Decide
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 font-display" data-testid="comparisons-title">
              Study Abroad Comparisons
            </h1>
            <p className="text-xl text-white/80 max-w-3xl mx-auto mb-8">
              Side-by-side comparisons to help you make informed decisions about destinations, programs, and degrees
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search comparisons (e.g., USA vs UK, MBA vs MS...)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 py-6 text-lg rounded-full bg-white border-0 shadow-lg"
                data-testid="comparisons-search"
              />
            </div>
          </div>
        </section>

        {/* Filter */}
        <section className={`py-6 border-b ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-center gap-4">
              <span className={`text-sm font-medium ${isDark ? 'text-white/80' : 'text-slate-700'}`}>Filter by type:</span>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className={`w-[200px] ${isDark ? 'bg-white/10 border-white/20' : ''}`} data-testid="filter-type">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Comparisons</SelectItem>
                  <SelectItem value="destination">Destination vs Destination</SelectItem>
                  <SelectItem value="program">Program vs Program</SelectItem>
                  <SelectItem value="degree">Degree vs Degree</SelectItem>
                </SelectContent>
              </Select>

              {selectedType !== 'all' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedType('all')}
                  className={isDark ? 'text-white/70 hover:text-white' : ''}
                >
                  Clear Filter
                </Button>
              )}
            </div>
          </div>
        </section>

        {/* Featured Comparisons */}
        {featuredComparisons.length > 0 && !searchTerm && selectedType === 'all' && (
          <section className="py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className={`text-2xl font-bold mb-6 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                <Star className="w-6 h-6 text-yellow-500" />
                Popular Comparisons
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {featuredComparisons.map(comp => (
                  <Link
                    key={comp.id}
                    to={`/compare/${comp.slug}`}
                    className="group"
                    data-testid={`featured-comparison-${comp.slug}`}
                  >
                    <Card className={`h-full transition-all duration-300 hover:shadow-xl ${
                      isDark
                        ? 'bg-gradient-to-br from-purple-500/20 to-blue-500/20 border-white/20 hover:border-purple-500/50'
                        : 'bg-gradient-to-br from-purple-50 to-blue-50 border-purple-200 hover:border-purple-400'
                    }`}>
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-3">
                          <Badge className="bg-yellow-500/20 text-yellow-600 border-yellow-500/30">
                            Featured
                          </Badge>
                          <Badge variant="outline" className={isDark ? 'border-white/30 text-white/80' : ''}>
                            {COMPARISON_TYPES[comp.comparison_type]?.label || comp.comparison_type}
                          </Badge>
                        </div>
                        <h3 className={`text-xl font-bold mb-2 group-hover:text-purple-500 transition-colors ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {comp.title}
                        </h3>
                        <p className={`text-sm mb-4 line-clamp-2 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                          {comp.tagline}
                        </p>
                        <div className="flex items-center gap-2">
                          <ArrowLeftRight className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                          <span className={`text-sm ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                            {comp.items?.length || 2} options compared
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* All Comparisons by Type */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
              </div>
            ) : filteredComparisons.length === 0 ? (
              <div className="text-center py-20">
                <Scale className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-white/20' : 'text-slate-300'}`} />
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  No comparisons found
                </h3>
                <p className={isDark ? 'text-white/60' : 'text-slate-500'}>
                  {searchTerm ? 'Try a different search term' : 'Comparisons will appear here once added'}
                </p>
              </div>
            ) : selectedType !== 'all' ? (
              // Show flat list when filtered
              <div className="space-y-6">
                <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {COMPARISON_TYPES[selectedType]?.label || 'Comparisons'} ({filteredComparisons.length})
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredComparisons.map(comp => (
                    <ComparisonCard key={comp.id} comparison={comp} isDark={isDark} />
                  ))}
                </div>
              </div>
            ) : (
              // Show grouped by type
              Object.entries(groupedComparisons).map(([type, comps]) => {
                const TypeIcon = COMPARISON_TYPES[type]?.icon;
                return (
                <div key={type} className="mb-12">
                  <div className="flex items-center gap-3 mb-6">
                    {TypeIcon && (
                      <TypeIcon className={`w-6 h-6 ${COMPARISON_TYPES[type].color}`} />
                    )}
                    <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {COMPARISON_TYPES[type]?.label || type}
                    </h2>
                    <Badge variant="secondary" className={isDark ? 'bg-white/10' : ''}>
                      {comps.length}
                    </Badge>
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {comps.map(comp => (
                      <ComparisonCard key={comp.id} comparison={comp} isDark={isDark} />
                    ))}
                  </div>
                </div>
              )})
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className={`py-16 ${isDark ? 'bg-white/5' : 'bg-slate-100'}`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className={`text-2xl md:text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Still Confused? Let Us Help!
            </h2>
            <p className={`mb-8 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
              Our expert counselors can provide personalized recommendations based on your profile, budget, and goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-gradient-to-r from-purple-500 to-blue-500 text-white"
                asChild
              >
                <Link to="/contact" data-testid="consultation-cta">
                  Book Free Consultation
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                asChild
                className={isDark ? 'border-white/30 text-white hover:bg-white/10' : ''}
              >
                <Link to="/programs" data-testid="programs-cta">
                  Browse All Programs
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

// Comparison Card Component
const ComparisonCard = ({ comparison, isDark }) => (
  <Link
    to={`/compare/${comparison.slug}`}
    className="group"
    data-testid={`comparison-card-${comparison.slug}`}
  >
    <Card className={`h-full transition-all duration-300 hover:shadow-lg ${
      isDark
        ? 'bg-white/5 border-white/10 hover:bg-white/10'
        : 'bg-white hover:shadow-xl'
    }`}>
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-3">
          {comparison.is_featured && (
            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
          )}
          <Badge variant="outline" className={`text-xs ${isDark ? 'border-white/30 text-white/70' : ''}`}>
            {comparison.items?.length || 2} compared
          </Badge>
        </div>

        <h3 className={`text-lg font-bold mb-2 group-hover:text-purple-500 transition-colors ${isDark ? 'text-white' : 'text-slate-900'}`}>
          {comparison.title}
        </h3>

        <p className={`text-sm mb-4 line-clamp-2 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
          {comparison.tagline}
        </p>

        <div className="flex items-center justify-between pt-4 border-t border-dashed">
          <div className="flex -space-x-2">
            {comparison.items?.slice(0, 3).map((item, idx) => (
              <div
                key={idx}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                  isDark 
                    ? 'bg-purple-500/30 border-[#1a2830] text-purple-300' 
                    : 'bg-purple-100 border-white text-purple-700'
                }`}
              >
                {item.name?.charAt(0) || idx + 1}
              </div>
            ))}
          </div>
          <ChevronRight className={`w-5 h-5 group-hover:translate-x-1 transition-transform ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
        </div>
      </CardContent>
    </Card>
  </Link>
);

export default ComparisonsPage;
